import { PatientLayout } from "@/components/layout/patient-layout";
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Pill,
  Droplets,
  ArrowLeft,
  ChevronRight,
  Copy,
  Check,
  Mail,
  Loader2,
  AlertTriangle,
  Clock,
  Search,
  CheckCircle2,
  FileText,
  CalendarDays,
  BookOpen,
  Stethoscope,
} from "lucide-react";
import { useListHospitals, useListPatientRequests } from "@workspace/api-client-react";
import { TeamCodeBadge } from "@/components/ui/same-team-badge";

type WorkflowType = "prescription-query" | "blood-form-request" | "appointment-query";

type Step = "screen" | "choose" | "hospital" | "details" | "review";

type MedicationForm = "tablet" | "injection" | "both" | "unsure";
type BloodTestDone = "yes" | "no" | "not_sure";

interface FormState {
  patientName: string;
  patientDob: string;
  nhsNumber: string;
  hospitalId: number | null;
  hospitalName: string;
  inboxId: number | null;
  inboxDisplayName: string;
  specialty: string;
  medicationName: string;
  queryDetails: string;
  testType: string;
  testNotes: string;
  // Structured prescription-query fields — patients don't have to remember
  // the drug name; the clinical team identifies it from these answers.
  medicationForm: MedicationForm | "";
  bloodTestDone: BloodTestDone | "";
  bloodTestDate: string;
  bloodTestLocation: string;
}

const INITIAL_FORM: FormState = {
  patientName: "",
  patientDob: "",
  nhsNumber: "",
  hospitalId: null,
  hospitalName: "",
  inboxId: null,
  inboxDisplayName: "",
  specialty: "rheumatology",
  medicationName: "",
  queryDetails: "",
  testType: "",
  testNotes: "",
  medicationForm: "",
  bloodTestDone: "",
  bloodTestDate: "",
  bloodTestLocation: "GP practice",
};

// Labels shown to patients while picking; the email body uses the same wording
// (mirrored on the server in formatPrescriptionQuery → formLabels) so the
// patient's review screen and the advice line email stay in lock-step.
const MEDICATION_FORM_LABELS: Record<MedicationForm, string> = {
  tablet: "Tablets",
  injection: "Injection",
  both: "Both tablets and injection",
  unsure: "Patient is not sure",
};

const BLOOD_TEST_LABELS: Record<BloodTestDone, string> = {
  yes: "Yes",
  no: "No",
  not_sure: "Not sure",
};

const TEST_TYPES = [
  "Full blood count (FBC)",
  "Urea & electrolytes (U&E)",
  "Liver function tests (LFTs)",
  "CRP / ESR (inflammatory markers)",
  "Methotrexate monitoring bloods (FBC + LFTs)",
  "Leflunomide monitoring bloods (FBC + LFTs)",
  "Hydroxychloroquine monitoring",
  "Azathioprine monitoring bloods (FBC + LFTs)",
  "Anti-TNF / biologic monitoring bloods",
  "JAK inhibitor monitoring bloods",
  "Vitamin D / calcium",
  "Thyroid function tests (TFTs)",
  "Uric acid level",
  "ANCA / ANA / complement levels",
  "Other (please specify in notes)",
];

function formatBody(type: WorkflowType, form: FormState, hospitalName: string): string {
  const lines: string[] = [];
  if (type === "prescription-query") {
    lines.push("PRESCRIPTION QUERY — RheumHub Patient Request");
    lines.push("=".repeat(48));
    lines.push("");
    lines.push(`Patient name:    ${form.patientName}`);
    if (form.patientDob) lines.push(`Date of birth:   ${form.patientDob}`);
    if (form.nhsNumber) lines.push(`NHS number:      ${form.nhsNumber}`);
    if (hospitalName) lines.push(`Hospital:        ${hospitalName}`);
    if (form.specialty && form.specialty !== "rheumatology") lines.push(`Specialty:       ${form.specialty.replace(/_/g, " ")}`);
    lines.push("");
    lines.push("MEDICATION DETAILS");
    lines.push("-".repeat(32));
    if (form.medicationForm) lines.push(`Type of medicine:   ${MEDICATION_FORM_LABELS[form.medicationForm as MedicationForm]}`);
    if (form.bloodTestDone) lines.push(`Recent blood test:  ${BLOOD_TEST_LABELS[form.bloodTestDone as BloodTestDone]}`);
    if (form.bloodTestDone === "yes") {
      if (form.bloodTestDate) lines.push(`Date of blood test: ${form.bloodTestDate}`);
      if (form.bloodTestLocation) lines.push(`Blood test done at: ${form.bloodTestLocation}`);
    }
    if (form.medicationName) lines.push(`Patient-named drug: ${form.medicationName}`);
    if (form.queryDetails) {
      lines.push("");
      lines.push("Anything else from the patient:");
      lines.push(form.queryDetails);
    }
  } else if (type === "blood-form-request") {
    lines.push("BLOOD FORM REQUEST — RheumHub Patient Request");
    lines.push("=".repeat(48));
    lines.push("");
    lines.push(`Patient name:    ${form.patientName}`);
    if (form.patientDob) lines.push(`Date of birth:   ${form.patientDob}`);
    if (form.nhsNumber) lines.push(`NHS number:      ${form.nhsNumber}`);
    if (hospitalName) lines.push(`Hospital:        ${hospitalName}`);
    lines.push("");
    lines.push("TEST DETAILS");
    lines.push("-".repeat(32));
    if (form.testType) lines.push(`Test required:   ${form.testType}`);
    if (form.testNotes) {
      lines.push("");
      lines.push("Additional notes:");
      lines.push(form.testNotes);
    }
  } else if (type === "appointment-query") {
    const apptLabels: Record<string, string> = {
      "when-is-my-appointment": "When is my appointment?",
      "need-to-reschedule": "Need to reschedule",
      "who-is-my-appointment-with": "Who is my appointment with?",
      "other": "Other appointment query",
    };
    lines.push("APPOINTMENT QUERY — RheumHub Patient Request");
    lines.push("=".repeat(48));
    lines.push("");
    lines.push(`Patient name:    ${form.patientName}`);
    if (form.patientDob) lines.push(`Date of birth:   ${form.patientDob}`);
    if (form.nhsNumber) lines.push(`NHS number:      ${form.nhsNumber}`);
    if (hospitalName) lines.push(`Hospital:        ${hospitalName}`);
    lines.push("");
    lines.push("APPOINTMENT DETAILS");
    lines.push("-".repeat(32));
    if (form.testType) lines.push(`Query type:      ${apptLabels[form.testType] ?? form.testType}`);
    if (form.testNotes) lines.push(`Appointment date: ${form.testNotes}`);
    if (form.queryDetails) {
      lines.push("");
      lines.push("Additional notes:");
      lines.push(form.queryDetails);
    }
  }
  lines.push("");
  lines.push("-".repeat(48));
  lines.push("Submitted via RheumHub patient portal");
  lines.push(`Date/time: ${new Date().toLocaleString("en-GB", { timeZone: "Europe/London" })} (UK time)`);
  return lines.join("\n");
}

export default function PatientRequests() {
  const [step, setStep] = useState<Step>("screen");
  const [workflowType, setWorkflowType] = useState<WorkflowType | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [hospitalSearch, setHospitalSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitted, setSubmitted] = useState<{ formattedBody: string; adviceLineEmail: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [inboxes, setInboxes] = useState<Array<{ id: number; displayName: string; email: string; specialty: string; sla?: string | null }>>([]);
  const [loadingInboxes, setLoadingInboxes] = useState(false);

  const { data: hospitals } = useListHospitals({ search: hospitalSearch || undefined });
  const { data: previousRequests, refetch: refetchRequests } = useListPatientRequests();

  const resolvedHospitalName = form.hospitalId && hospitals
    ? (hospitals.find((h) => h.id === form.hospitalId)?.name ?? form.hospitalName)
    : form.hospitalName;

  const previewBody = workflowType
    ? formatBody(workflowType, form, resolvedHospitalName)
    : "";

  const handleChoose = (type: WorkflowType) => {
    setWorkflowType(type);
    setStep("hospital");
  };

  const handleBack = useCallback(() => {
    if (step === "hospital") { setStep("screen"); setWorkflowType(null); }
    else if (step === "details") setStep("hospital");
    else if (step === "review") setStep("details");
  }, [step]);

  const handleReset = () => {
    setStep("choose");
    setWorkflowType(null);
    setForm(INITIAL_FORM);
    setHospitalSearch("");
    setSubmitted(null);
    setCopied(false);
    setSubmitError(false);
    setInboxes([]);
    refetchRequests();
  };

  const handleSubmit = async () => {
    if (!workflowType) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const body: Record<string, unknown> = {
        requestType: workflowType,
        patientName: form.patientName,
        patientDob: form.patientDob || null,
        nhsNumber: form.nhsNumber || null,
        hospitalId: form.hospitalId || null,
        hospitalName: resolvedHospitalName || null,
        inboxId: form.inboxId || null,
        specialty: form.specialty || "rheumatology",
        medicationName: form.medicationName || null,
        queryDetails: form.queryDetails || null,
        testType: form.testType || null,
        testNotes: form.testNotes || null,
        medicationForm: form.medicationForm || null,
        bloodTestDone: form.bloodTestDone || null,
        bloodTestDate: form.bloodTestDate || null,
        bloodTestLocation: form.bloodTestDone === "yes" ? (form.bloodTestLocation || null) : null,
        // Appointment query fields map from generic form fields
        appointmentQueryType: workflowType === "appointment-query" ? (form.testType || null) : undefined,
        appointmentDate: workflowType === "appointment-query" ? (form.testNotes || null) : undefined,
      };
      const response = await fetch("/api/patient/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error("Submit failed");
      const result = await response.json();
      (window as any).umami?.track?.("request-submitted", { category: workflowType });
      setSubmitted({ formattedBody: result.formattedBody, adviceLineEmail: result.adviceLineEmail });
    } catch (_err) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!submitted) return;
    await navigator.clipboard.writeText(submitted.formattedBody);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const mailtoHref = submitted
    ? (() => {
        const subject = workflowType === "prescription-query"
          ? `Prescription Query — ${form.patientName}`
          : workflowType === "blood-form-request"
            ? `Blood Form Request — ${form.patientName}`
            : `Appointment Query — ${form.patientName}`;
        const params = new URLSearchParams({ subject, body: submitted.formattedBody });
        const to = submitted.adviceLineEmail ?? "";
        return `mailto:${to}?${params.toString()}`;
      })()
    : "#";

  const filteredHospitals = hospitalSearch.length >= 2
    ? (hospitals ?? []).slice(0, 12)
    : [];

  const isDetailsValid = workflowType === "prescription-query"
    ? form.patientName.trim().length > 0
        && form.medicationForm !== ""
        && form.bloodTestDone !== ""
        && (form.bloodTestDone !== "yes" || form.bloodTestLocation.trim().length > 0)
    : workflowType === "blood-form-request"
      ? form.patientName.trim().length > 0 && form.testType.trim().length > 0
      : form.patientName.trim().length > 0; // appointment-query only needs name

  return (
    <PatientLayout title="Contact Advice Line">
      {/* Success state */}
      {submitted ? (
        <div className="max-w-2xl space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-3xl p-6 flex items-start gap-4">
            <CheckCircle2 className="h-7 w-7 text-green-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Request saved</h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                {submitted.adviceLineEmail
                  ? `Your request has been sent to your advice line (${submitted.adviceLineEmail}) and saved to your history.`
                  : "Your request has been saved. Use the options below to send it to your advice line."}
              </p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Your formatted message</p>
              <Button variant="ghost" size="sm" onClick={handleCopy} className="text-slate-500 hover:text-slate-900 gap-1.5">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <pre className="text-xs text-slate-600 bg-slate-50 rounded-2xl p-4 whitespace-pre-wrap leading-relaxed font-mono border border-slate-100">
              {submitted.formattedBody}
            </pre>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={mailtoHref}
              className="inline-flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-medium rounded-full px-6 py-3 text-sm transition-colors"
            >
              <Mail className="h-4 w-4" />
              Open in email client
              {submitted.adviceLineEmail && (
                <span className="text-rose-200 text-xs">({submitted.adviceLineEmail})</span>
              )}
            </a>
            <Button variant="outline" onClick={handleReset} className="rounded-full px-6">
              Submit another request
            </Button>
          </div>

          {!submitted.adviceLineEmail && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
              <p>
                No advice line email was found for your selected hospital. Use the "Open in email client" button to send manually, or call the advice line using the number on your appointment letter.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Step: Screen — what's this about? */}
          {step === "screen" && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">What can we help you with?</h1>
                <p className="text-slate-600 leading-relaxed">
                  Select the option that best describes your query. This helps us route you to the right place.
                </p>
              </div>

              <div className="grid gap-3">
                {/* Prescription / Medication */}
                <button
                  onClick={() => { setWorkflowType("prescription-query"); setStep("hospital"); }}
                  className="text-left rounded-3xl p-5 border border-violet-200 bg-violet-50 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-2xl bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Pill className="h-5 w-5 text-violet-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Prescription or medication question</h3>
                        <p className="text-sm text-slate-600">Repeat prescriptions, dosage changes, running out of supply</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-violet-400 group-hover:translate-x-1 transition-transform shrink-0 mt-1" />
                  </div>
                </button>

                {/* Blood test form */}
                <button
                  onClick={() => { setWorkflowType("blood-form-request"); setStep("hospital"); }}
                  className="text-left rounded-3xl p-5 border border-teal-200 bg-teal-50 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-2xl bg-teal-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Droplets className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Blood test form request</h3>
                        <p className="text-sm text-slate-600">Request a blood test form for your monitoring bloods</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform shrink-0 mt-1" />
                  </div>
                </button>

                {/* Appointment query — proceed to form */}
                <button
                  onClick={() => { setWorkflowType("appointment-query"); setStep("hospital"); }}
                  className="text-left rounded-3xl p-5 border border-amber-200 bg-amber-50 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                        <CalendarDays className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Appointment or booking query</h3>
                        <p className="text-sm text-slate-600">Check my appointment date, reschedule, or who I'm seeing</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-amber-400 group-hover:translate-x-1 transition-transform shrink-0 mt-1" />
                  </div>
                </button>

                {/* General advice — redirect to FAQ */}
                <a
                  href="/patient/advice-faq"
                  className="block text-left rounded-3xl p-5 border border-sky-200 bg-sky-50 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-2xl bg-sky-100 flex items-center justify-center shrink-0 mt-0.5">
                        <BookOpen className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">General advice about my condition</h3>
                        <p className="text-sm text-slate-600">Side effects, diet, exercise, flu jab, what to expect</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-sky-400 group-hover:translate-x-1 transition-transform shrink-0 mt-1" />
                  </div>
                </a>

                {/* Test results */}
                <div className="rounded-3xl p-5 border border-slate-200 bg-slate-50">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                      <FileText className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Blood test results</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Blood test results are usually available within a few days. If you had your test more than a 
                        week ago and haven't heard back, call your GP surgery. For urgent results, contact your 
                        rheumatology advice line by phone.
                      </p>
                    </div>
                  </div>
                </div>

                {/* GP / pharmacy */}
                <div className="rounded-3xl p-5 border border-slate-200 bg-slate-50">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Stethoscope className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">GP or pharmacy matter</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Your rheumatology advice line handles rheumatology-specific queries. For general medical 
                        matters, non-rheumatology medications, or pharmacy supply issues, please contact your GP 
                        surgery directly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-600 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                  <strong className="text-slate-700">Urgent?</strong> If you need urgent medical help, call{" "}
                  <a href="tel:999" className="font-semibold underline text-slate-700 hover:text-slate-900">999</a>.
                  For non-urgent medical advice, call{" "}
                  <a href="tel:111" className="font-semibold underline text-slate-700 hover:text-slate-900">NHS 111</a>.
                </p>
              </div>
            </div>
          )}

          {/* Step: Choose workflow */}
          {step === "choose" && (
            <div className="space-y-6 max-w-2xl">
              <p className="text-slate-600 leading-relaxed">
                Use these forms to contact your rheumatology advice line about a prescription or blood test. Your request will be formatted and sent to your hospital team.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  onClick={() => handleChoose("prescription-query")}
                  className="text-left rounded-3xl p-6 border bg-violet-50 border-violet-200 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-2xl bg-violet-100 flex items-center justify-center">
                      <Pill className="h-6 w-6 text-violet-600" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-violet-400 group-hover:translate-x-1 transition-transform mt-1" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Prescription Query</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Questions about your medication, repeat prescriptions, dosage changes, or running out of supply.
                  </p>
                </button>

                <button
                  onClick={() => handleChoose("blood-form-request")}
                  className="text-left rounded-3xl p-6 border bg-teal-50 border-teal-200 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-2xl bg-teal-100 flex items-center justify-center">
                      <Droplets className="h-6 w-6 text-teal-600" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform mt-1" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Blood Form Request</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Request a blood test form for monitoring bloods required by your rheumatology team.
                  </p>
                </button>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Please note</p>
                <p className="leading-relaxed">
                  These forms contact your advice line by email. For urgent concerns, call your advice line directly using the number on your appointment letter, or call{" "}
                  <a href="tel:111" className="font-medium underline text-slate-700 hover:text-slate-900">NHS 111</a>.
                </p>
              </div>

              {/* Previous requests */}
              {previousRequests && previousRequests.length > 0 && (
                <div>
                  <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-slate-400" />
                    Previous requests
                  </h2>
                  <div className="space-y-3">
                    {previousRequests.slice(0, 5).map((r) => (
                      <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${r.requestType === "prescription-query" ? "bg-violet-50" : "bg-teal-50"}`}>
                          {r.requestType === "prescription-query" ? (
                            <Pill className="h-5 w-5 text-violet-600" />
                          ) : (
                            <Droplets className="h-5 w-5 text-teal-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {r.requestType === "prescription-query" ? "Prescription Query" : "Blood Form Request"}
                            {r.medicationName || r.testType ? ` — ${r.medicationName ?? r.testType}` : ""}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                            {r.adviceLineEmail ? ` · Sent to ${r.adviceLineEmail}` : " · Not sent by email"}
                          </p>
                          {/* Tracking status */}
                          {((r.requestType === "prescription-query" && r.prescriptionStatus) || (r.requestType === "blood-form-request" && r.bloodFormStatus)) && (
                            <p className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <CheckCircle2 className="h-3 w-3" />
                              {r.requestType === "prescription-query"
                                ? (r.prescriptionStatus === "acknowledged" ? "Acknowledged"
                                  : r.prescriptionStatus === "no_recent_bloods" ? "No recent bloods"
                                  : r.prescriptionStatus === "sent_to_pharmacy" ? "Sent to pharmacy"
                                  : r.prescriptionStatus === "contact_supplier" ? "Contacting supplier"
                                  : r.prescriptionStatus === "ready_for_collection" ? "Ready for collection"
                                  : r.prescriptionStatus === "custom" ? r.trackingNote ?? "In progress"
                                  : r.prescriptionStatus)
                                : "Sent via post"}
                            </p>
                          )}
                          {(r.assignedToName || r.assignedToTeamCode) && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <p className="text-xs text-slate-600">
                                Looked after by{" "}
                                <span className="font-medium text-slate-700">
                                  {r.assignedToName ?? "your rheumatology team"}
                                </span>
                              </p>
                              <TeamCodeBadge teamCode={r.assignedToTeamCode} />
                            </div>
                          )}
                        </div>
                        <FileText className="h-4 w-4 text-slate-300 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Hospital selection */}
          {step === "hospital" && (
            <div className="max-w-2xl space-y-5">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={handleBack} className="text-slate-500 hover:text-slate-900 -ml-2">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${workflowType === "prescription-query" ? "bg-violet-50 border-violet-200 text-violet-700" : "bg-teal-50 border-teal-200 text-teal-700"}`}>
                  {workflowType === "prescription-query" ? <Pill className="h-4 w-4" /> : <Droplets className="h-4 w-4" />}
                  {workflowType === "prescription-query" ? "Prescription Query" : "Blood Form Request"}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Which hospital looks after you?</h2>
                <p className="text-slate-500 text-sm">This helps us find the right advice line email address. You can skip this step.</p>
              </div>

              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={hospitalSearch}
                  onChange={(e) => setHospitalSearch(e.target.value)}
                  placeholder="Search by hospital name or city..."
                  className="pl-10 rounded-2xl h-12"
                />
              </div>

              {filteredHospitals.length > 0 && !form.inboxId && (
                <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
                  {filteredHospitals.map((h) => (
                    <button
                      key={h.id}
                      onClick={async () => {
                        setForm((f) => ({ ...f, hospitalId: h.id, hospitalName: h.name }));
                        // Fetch advice line inboxes for this hospital
                        setLoadingInboxes(true);
                        setInboxes([]);
                        try {
                          const res = await fetch(`/api/directory/hospitals/${h.id}/inboxes`);
                          if (res.ok) {
                            const data = await res.json();
                            setInboxes(data);
                            // If only one inbox, auto-select it
                            if (data.length === 1) {
                              setForm((f) => ({ ...f, inboxId: data[0].id, inboxDisplayName: data[0].displayName, specialty: data[0].specialty || "rheumatology" }));
                              setStep("details");
                            }
                          }
                        } catch (_err) {
                          // Silently fail — fall back to old flow
                        } finally {
                          setLoadingInboxes(false);
                        }
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between group ${form.hospitalId === h.id ? "bg-slate-50" : ""}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{h.name}</p>
                        <p className="text-xs text-slate-500">{h.nhsTrustName} · {h.city}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {/* Inbox selection — shown after hospital is picked and inboxes load */}
              {inboxes.length > 1 && (
                <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
                  <div className="px-4 py-3 bg-slate-50">
                    <p className="text-sm font-semibold text-slate-700">
                      {form.hospitalName}
                    </p>
                    <p className="text-xs text-slate-500">Which service do you need?</p>
                  </div>
                  {inboxes.map((inbox) => (
                    <button
                      key={inbox.id}
                      onClick={() => {
                        setForm((f) => ({ ...f, inboxId: inbox.id, inboxDisplayName: inbox.displayName, specialty: inbox.specialty || "rheumatology" }));
                        setStep("details");
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between group"
                    >
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="font-medium text-slate-900 text-sm">{inbox.displayName}</p>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-200 capitalize">
                            {inbox.specialty?.replace(/_/g, " ") ?? "rheumatology"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">{inbox.email}</p>
                        {inbox.sla && (
                          <p className="text-xs text-teal-600 mt-0.5">Usually replies within {inbox.sla}</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {hospitalSearch.length >= 2 && filteredHospitals.length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">No hospitals found for "{hospitalSearch}"</p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  onClick={() => setStep("details")}
                  variant="outline"
                  className="rounded-full"
                >
                  Skip — my hospital isn't listed
                </Button>
                {form.hospitalId && (
                  <Button
                    onClick={() => setStep("details")}
                    className={`rounded-full ${workflowType === "prescription-query" ? "bg-violet-600 hover:bg-violet-700" : "bg-teal-600 hover:bg-teal-700"}`}
                  >
                    Continue with {resolvedHospitalName}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Step: Details form */}
          {step === "details" && (
            <div className="max-w-2xl space-y-6">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={handleBack} className="text-slate-500 hover:text-slate-900 -ml-2">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${workflowType === "prescription-query" ? "bg-violet-50 border-violet-200 text-violet-700" : "bg-teal-50 border-teal-200 text-teal-700"}`}>
                  {workflowType === "prescription-query" ? <Pill className="h-4 w-4" /> : <Droplets className="h-4 w-4" />}
                  {workflowType === "prescription-query" ? "Prescription Query" : "Blood Form Request"}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Your details</h2>
                <p className="text-slate-500 text-sm">This information helps the advice line team identify your records.</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="patientName" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Your full name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="patientName"
                    value={form.patientName}
                    onChange={(e) => setForm((f) => ({ ...f, patientName: e.target.value }))}
                    placeholder="e.g. Jane Smith"
                    className="rounded-2xl"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dob" className="text-sm font-medium text-slate-700 mb-1.5 block">Date of birth</Label>
                    <Input
                      id="dob"
                      value={form.patientDob}
                      onChange={(e) => setForm((f) => ({ ...f, patientDob: e.target.value }))}
                      placeholder="DD/MM/YYYY"
                      className="rounded-2xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nhs" className="text-sm font-medium text-slate-700 mb-1.5 block">NHS number</Label>
                    <Input
                      id="nhs"
                      value={form.nhsNumber}
                      onChange={(e) => setForm((f) => ({ ...f, nhsNumber: e.target.value }))}
                      placeholder="e.g. 485 777 3456"
                      className="rounded-2xl"
                    />
                  </div>
                </div>

                {!form.hospitalId && (
                  <div>
                    <Label htmlFor="hospitalName" className="text-sm font-medium text-slate-700 mb-1.5 block">Hospital / rheumatology unit</Label>
                    <Input
                      id="hospitalName"
                      value={form.hospitalName}
                      onChange={(e) => setForm((f) => ({ ...f, hospitalName: e.target.value }))}
                      placeholder="e.g. Royal National Hospital for Rheumatic Diseases"
                      className="rounded-2xl"
                    />
                  </div>
                )}
                {form.hospitalId && resolvedHospitalName && (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700">{resolvedHospitalName}</p>
                      <p className="text-xs text-slate-500">Selected hospital</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setForm((f) => ({ ...f, hospitalId: null, hospitalName: "" })); setStep("hospital"); }}
                      className="text-slate-400 hover:text-slate-700 text-xs"
                    >
                      Change
                    </Button>
                  </div>
                )}
              </div>

              {/* Type-specific fields */}
              {workflowType === "prescription-query" ? (
                <div className="space-y-5 pt-2">
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">A few quick questions</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      You don't need to remember the name of your medication — your rheumatology team will look it up. These questions just help them find the right one.
                    </p>
                  </div>

                  {/* Q1: tablet or injection */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Is the medicine you're asking about a tablet or an injection? <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {(Object.keys(MEDICATION_FORM_LABELS) as MedicationForm[]).map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, medicationForm: value }))}
                          className={`text-left px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                            form.medicationForm === value
                              ? "bg-violet-50 border-violet-300 text-violet-900 font-medium"
                              : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {form.medicationForm === value && <Check className="h-3.5 w-3.5 inline mr-1.5 text-violet-600" />}
                          {MEDICATION_FORM_LABELS[value]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Q2: blood test done */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Have you had a blood test recently? <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-slate-500 mb-2">Most rheumatology medications need an up-to-date blood test before a new prescription.</p>
                    <div className="grid grid-cols-3 gap-2">
                      {(Object.keys(BLOOD_TEST_LABELS) as BloodTestDone[]).map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setForm((f) => ({
                            ...f,
                            bloodTestDone: value,
                            // Clear date/location when switching away from "yes" so a stale
                            // date entered earlier can't sneak into the email body.
                            bloodTestDate: value === "yes" ? f.bloodTestDate : "",
                            bloodTestLocation: value === "yes" ? f.bloodTestLocation : "GP practice",
                          }))}
                          className={`text-center px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                            form.bloodTestDone === value
                              ? "bg-violet-50 border-violet-300 text-violet-900 font-medium"
                              : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {BLOOD_TEST_LABELS[value]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Q2a: blood test date + location (only if yes) */}
                  {form.bloodTestDone === "yes" && (
                    <div className="space-y-4 bg-violet-50/50 border border-violet-100 rounded-2xl p-4">
                      <div>
                        <Label htmlFor="bloodDate" className="text-sm font-medium text-slate-700 mb-1.5 block">
                          When was the blood test done? <span className="text-slate-400 font-normal">(approximate is fine)</span>
                        </Label>
                        <Input
                          id="bloodDate"
                          type="date"
                          value={form.bloodTestDate}
                          onChange={(e) => setForm((f) => ({ ...f, bloodTestDate: e.target.value }))}
                          className="rounded-2xl"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">
                          Where was it done? <span className="text-red-500">*</span>
                        </Label>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          {["GP practice", "Hospital"].map((loc) => (
                            <button
                              key={loc}
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, bloodTestLocation: loc }))}
                              className={`text-left px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                                form.bloodTestLocation === loc
                                  ? "bg-violet-50 border-violet-300 text-violet-900 font-medium"
                                  : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 bg-white"
                              }`}
                            >
                              {form.bloodTestLocation === loc && <Check className="h-3.5 w-3.5 inline mr-1.5 text-violet-600" />}
                              {loc}
                            </button>
                          ))}
                        </div>
                        <Input
                          value={["GP practice", "Hospital"].includes(form.bloodTestLocation) ? "" : form.bloodTestLocation}
                          onChange={(e) => setForm((f) => ({ ...f, bloodTestLocation: e.target.value }))}
                          placeholder="Or type somewhere else (e.g. community phlebotomy clinic)"
                          className="rounded-2xl"
                        />
                      </div>
                    </div>
                  )}

                  {/* Optional free text */}
                  <div>
                    <Label htmlFor="queryDetails" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Anything else you'd like the team to know? <span className="text-slate-400 font-normal">(optional)</span>
                    </Label>
                    <Textarea
                      id="queryDetails"
                      value={form.queryDetails}
                      onChange={(e) => setForm((f) => ({ ...f, queryDetails: e.target.value }))}
                      placeholder="e.g. I've nearly run out, my GP says rheumatology needs to prescribe, side effect questions…"
                      className="rounded-2xl min-h-[100px] resize-none"
                    />
                  </div>
                </div>
              ) : workflowType === "appointment-query" ? (
                <div className="space-y-4 pt-2">
                  <h3 className="text-base font-bold text-slate-900">Appointment details</h3>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">What do you need to know?</Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        { value: "when-is-my-appointment", label: "When is my appointment?" },
                        { value: "need-to-reschedule", label: "I need to reschedule" },
                        { value: "who-is-my-appointment-with", label: "Who am I seeing?" },
                        { value: "other", label: "Something else" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, testType: opt.value }))}
                          className={`text-left px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                            form.testType === opt.value
                              ? "bg-amber-50 border-amber-300 text-amber-900 font-medium"
                              : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {form.testType === opt.value && <Check className="h-3.5 w-3.5 inline mr-1.5 text-amber-600" />}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="testNotes" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Appointment date <span className="text-slate-400 font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="testNotes"
                      type="date"
                      value={form.testNotes}
                      onChange={(e) => setForm((f) => ({ ...f, testNotes: e.target.value }))}
                      className="rounded-2xl h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="queryDetails" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Anything else? <span className="text-slate-400 font-normal">(optional)</span>
                    </Label>
                    <Textarea
                      id="queryDetails"
                      value={form.queryDetails}
                      onChange={(e) => setForm((f) => ({ ...f, queryDetails: e.target.value }))}
                      placeholder="Any additional information for the team..."
                      className="rounded-2xl min-h-[100px] resize-none"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 pt-2">
                  <h3 className="text-base font-bold text-slate-900">Blood test details</h3>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Test required <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {TEST_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, testType: t }))}
                          className={`text-left px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                            form.testType === t
                              ? "bg-teal-50 border-teal-300 text-teal-900 font-medium"
                              : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {form.testType === t && <Check className="h-3.5 w-3.5 inline mr-1.5 text-teal-600" />}
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="testNotes" className="text-sm font-medium text-slate-700 mb-1.5 block">Additional notes</Label>
                    <Textarea
                      id="testNotes"
                      value={form.testNotes}
                      onChange={(e) => setForm((f) => ({ ...f, testNotes: e.target.value }))}
                      placeholder="Any additional information for the team — e.g. specific tube requirements, urgency, or context..."
                      className="rounded-2xl min-h-[100px] resize-none"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setStep("review")}
                  disabled={!isDetailsValid}
                  className={`rounded-full px-8 ${workflowType === "prescription-query" ? "bg-violet-600 hover:bg-violet-700 disabled:bg-violet-200" : "bg-teal-600 hover:bg-teal-700 disabled:bg-teal-200"}`}
                >
                  Review request
                </Button>
              </div>
            </div>
          )}

          {/* Step: Review */}
          {step === "review" && (
            <div className="max-w-2xl space-y-6">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={handleBack} className="text-slate-500 hover:text-slate-900 -ml-2">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${workflowType === "prescription-query" ? "bg-violet-50 border-violet-200 text-violet-700" : "bg-teal-50 border-teal-200 text-teal-700"}`}>
                  {workflowType === "prescription-query" ? <Pill className="h-4 w-4" /> : <Droplets className="h-4 w-4" />}
                  {workflowType === "prescription-query" ? "Prescription Query" : "Blood Form Request"}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Review your request</h2>
                <p className="text-slate-500 text-sm">Check the details below before sending. This is exactly what will be forwarded to your advice line.</p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-5">
                <pre className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed font-mono">
                  {previewBody}
                </pre>
              </div>

              {submitError && (
                <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Something went wrong. Please try again or call your advice line directly.
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`rounded-full px-8 gap-2 ${workflowType === "prescription-query" ? "bg-violet-600 hover:bg-violet-700" : "bg-teal-600 hover:bg-teal-700"}`}
                >
                  {isSubmitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <><Mail className="h-4 w-4" /> Submit request</>
                  )}
                </Button>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                Submitting will save your request and, if your hospital's advice line email is available, send it automatically. You can also copy the text and email them directly.
              </p>
            </div>
          )}
        </>
      )}
    </PatientLayout>
  );
}
